import React, { createContext, useContext } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, DollarSign, TrendingUp, Activity, Users, Truck, Heart,
  Sparkles, FileText, Settings, Scale, Rocket, History, BarChart3,
  GraduationCap, UserCog, FolderLock, UserCircle,
  ChevronLeft, ChevronRight, CreditCard,
  UserCheck, Mail, Phone,
} from 'lucide-react';
import {
  canManageUsers,
  getAgentLandingPath,
  getSingleAssignedAgentWithoutImageIPath,
  hasImageILicense,
  isAgentOnlyAccount,
  isOwnerAccount,
} from '../lib/access';

export const SidebarContext = createContext({ collapsed: false, setCollapsed: () => {} });

const dashboardItems = [
  { title: 'Overview', url: '/imagei/overview', icon: LayoutDashboard },
  { title: 'Finance', url: '/imagei/finance', icon: DollarSign },
  { title: 'Sales', url: '/imagei/sales', icon: TrendingUp },
  { title: 'Operations', url: '/imagei/operations', icon: Activity },
  { title: 'Human Capital', url: '/imagei/hr', icon: Users },
  { title: 'Supply Chain', url: '/imagei/supply', icon: Truck },
  { title: 'Customer', url: '/imagei/customer', icon: Heart },
];

const intelligenceItems = [
  { title: 'Reconciliation', url: '/imagei/reconciliation', icon: Scale },
  { title: 'Historical & Trends', url: '/imagei/historical', icon: History },
  { title: 'Benchmarks & News', url: '/imagei/benchmarks', icon: BarChart3 },
  { title: 'AI Agents', url: '/imagei/agents', icon: Sparkles },
  { title: 'Reports', url: '/imagei/reports', icon: FileText },
];

const workspaceItems = [
  { title: 'Onboarding', url: '/imagei/onboarding', icon: Rocket },
  { title: 'Training', url: '/imagei/training', icon: GraduationCap },
  { title: 'My Profile', url: '/imagei/profile', icon: UserCircle },
  { title: 'Settings', url: '/imagei/settings', icon: Settings },
];

function SidebarGroup({ label, items, collapsed }) {
  const location = useLocation();
  const { setCollapsed } = useContext(SidebarContext);

  const closeMobileSidebar = () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setCollapsed(true);
    }
  };

  return (
    <div className="ih-sidebar-group">
      {!collapsed && (
        <div className="ih-sidebar-group-label">{label}</div>
      )}
      {items.map((item) => {
        const isActive = location.pathname === item.url || location.pathname.startsWith(item.url + '/');
        return (
          <NavLink
            key={item.title}
            to={item.url}
            className={`ih-sidebar-item ${isActive ? 'active' : ''}`}
            style={{ textDecoration: 'none' }}
            title={collapsed ? item.title : undefined}
            onClick={closeMobileSidebar}
          >
            <item.icon style={{ width: 16, height: 16, flexShrink: 0 }} />
            {!collapsed && <span>{item.title}</span>}
          </NavLink>
        );
      })}
    </div>
  );
}

export function AppSidebar() {
  const { collapsed, setCollapsed } = useContext(SidebarContext);
  const account = useSelector((state) => state.account || {});
  const orgMember = account?.org_member || {};
  const superAdmin = canManageUsers(account);
  const imageIEnabled = hasImageILicense(account);
  const canSeeReportAllocation = superAdmin && imageIEnabled;
  const singleAgentPath = getSingleAssignedAgentWithoutImageIPath(account);
  const agentOnly = !imageIEnabled || Boolean(singleAgentPath) || isAgentOnlyAccount(account);
  const agentLandingPath = singleAgentPath || getAgentLandingPath(account);
  const headerUrl = agentOnly ? agentLandingPath : '/imagei/overview';
  const visibleIntelligenceItems = agentOnly
    ? intelligenceItems
      .filter((item) => item.url === '/imagei/agents')
      .map((item) => ({ ...item, url: agentLandingPath }))
    : intelligenceItems;
  const adminItems = [
    ...(isOwnerAccount(account) ? [{ title: 'Manage Subscription', url: '/imagei/admin/subscriptions', icon: CreditCard }] : []),
    ...(superAdmin ? [{ title: 'User Allocation', url: '/imagei/admin/users', icon: UserCog }] : []),
    ...(canSeeReportAllocation ? [{ title: 'Report Allocation', url: '/imagei/admin/reports', icon: FolderLock }] : []),
  ];
  const visibleWorkspaceItems = agentOnly
    ? workspaceItems.filter((item) => item.url === '/imagei/profile' || item.url === '/imagei/settings')
    : workspaceItems;
  const plannerName = orgMember.business_planner_name || orgMember.planner_name || null;
  const plannerEmail = orgMember.business_planner_email || orgMember.planner_email || null;
  const plannerPhone = orgMember.business_planner_phone || orgMember.planner_phone || null;
  const plannerRole = orgMember.business_planner_role || orgMember.planner_role || 'Business Planner';
  const plannerInitials = plannerName
    ? plannerName.split(' ').map((word) => word[0]).slice(0, 2).join('').toUpperCase()
    : 'BP';
  const hasPlannerInfo = Boolean(plannerName || plannerEmail);

  return (
    <div className={`ih-sidebar scrollbar-thin ${collapsed ? 'collapsed' : ''}`} style={{ position: 'relative' }}>

      {/* Header */}
      <div className="ih-sidebar-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <NavLink to={headerUrl} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, flexShrink: 0,
            background: 'var(--gradient-primary)',
            display: 'grid', placeItems: 'center',
            boxShadow: 'var(--shadow-glow)',
          }}>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 700, color: '#fff' }}>i.</span>
          </div>
          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 600, color: 'var(--sidebar-foreground)', letterSpacing: '-0.01em' }}>IMAGE-I</span>
              <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--muted-foreground)' }}>Reporting Suite</span>
            </div>
          )}
        </NavLink>
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--muted-foreground)', padding: 4, borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed
            ? <ChevronRight style={{ width: 15, height: 15 }} />
            : <ChevronLeft style={{ width: 15, height: 15 }} />}
        </button>
      </div>

      {/* Content */}
      <div className="ih-sidebar-content">
        {!agentOnly && <SidebarGroup label="Dashboards" items={dashboardItems} collapsed={collapsed} />}
        <SidebarGroup label="Intelligence" items={visibleIntelligenceItems} collapsed={collapsed} />
        <SidebarGroup label="Workspace" items={visibleWorkspaceItems} collapsed={collapsed} />
        {adminItems.length > 0 && <SidebarGroup label="Administration" items={adminItems} collapsed={collapsed} />}
      </div>

      {/* Footer — pipeline health */}
      {!agentOnly && <div className="ih-sidebar-footer" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {hasPlannerInfo && !collapsed && (
          <div className="surface-card" style={{ padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <UserCheck style={{ width: 12, height: 12, color: 'var(--primary)', flexShrink: 0 }} />
              <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'var(--muted-foreground)', fontWeight: 600 }}>
                Business Planner
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                background: 'var(--gradient-primary)',
                display: 'grid', placeItems: 'center',
              }}>
                <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 700, color: '#fff' }}>
                  {plannerInitials}
                </span>
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--foreground)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {plannerName || 'Not assigned'}
                </p>
                <p style={{ fontSize: 10, color: 'var(--muted-foreground)', margin: '1px 0 0' }}>{plannerRole}</p>
              </div>
            </div>
            {plannerEmail && (
              <a href={`mailto:${plannerEmail}`} style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, fontSize: 10, color: 'var(--muted-foreground)', textDecoration: 'none' }}>
                <Mail style={{ width: 10, height: 10 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{plannerEmail}</span>
              </a>
            )}
            {plannerPhone && (
              <a href={`tel:${plannerPhone}`} style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3, fontSize: 10, color: 'var(--muted-foreground)', textDecoration: 'none' }}>
                <Phone style={{ width: 10, height: 10 }} />
                {plannerPhone}
              </a>
            )}
          </div>
        )}

        {hasPlannerInfo && collapsed && (
          <div style={{ display: 'flex', justifyContent: 'center' }} title={plannerName || 'Business Planner'}>
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              background: 'var(--gradient-primary)',
              display: 'grid', placeItems: 'center',
            }}>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 700, color: '#fff' }}>
                {plannerInitials}
              </span>
            </div>
          </div>
        )}

        {!collapsed && (
          <div className="surface-card" style={{ padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: 'var(--success)',
                animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite',
              }} />
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--foreground)' }}>Pipelines healthy</span>
            </div>
            <p style={{ marginTop: 4, fontSize: 11, color: 'var(--muted-foreground)' }}>Last sync · 2 min ago</p>
          </div>
        )}
      </div>}
    </div>
  );
}
